import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Crown, Users, Building2, DollarSign, TrendingUp,
  Loader2, ChevronRight, AlertTriangle, CheckCircle2, Clock, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const ReferralLeaderboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/analytics/referral-leaderboard');
      setData(res.data);
    } catch {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { top_referrers = [], breakdown = {}, funnel = {}, liability = {} } = data || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="referral-leaderboard">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/analytics')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <button onClick={() => navigate('/admin/home')} className="text-sm font-bold text-[#03DFFC] hover:opacity-80 hidden sm:inline">iEvntür</button>
            <h1 className="text-lg font-semibold text-gray-900">Referral Leaderboard</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/analytics')}>
            <BarChart3 className="w-4 h-4 mr-1.5" />Analytics
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Bonus Liability Tracker */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Bonus Liability Tracker</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="bonus-liability">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">${liability.pending_payouts?.toFixed(2)}</p>
                  <p className="text-sm text-amber-600">Pending Payouts</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">${liability.approved_payouts?.toFixed(2)}</p>
                  <p className="text-sm text-gray-500">Approved Payouts</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">${liability.total_paid_out?.toFixed(2)}</p>
                  <p className="text-sm text-green-600">Total Paid Out</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Referral Funnel + Breakdown side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Referral Funnel */}
          <Card data-testid="referral-funnel">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Referral Funnel</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="space-y-3">
                {[
                  { label: 'Total Referrals', value: funnel.total_referrals, color: 'bg-indigo-500', width: 100 },
                  { label: 'Signed Up', value: funnel.signed_up, color: 'bg-cyan-500', width: funnel.total_referrals ? (funnel.signed_up / funnel.total_referrals) * 100 : 0 },
                  { label: 'Profile Completed', value: funnel.profile_completed, color: 'bg-teal-500', width: funnel.total_referrals ? (funnel.profile_completed / funnel.total_referrals) * 100 : 0 },
                  { label: 'First Gig Done', value: funnel.first_gig_completed, color: 'bg-amber-500', width: funnel.total_referrals ? (funnel.first_gig_completed / funnel.total_referrals) * 100 : 0 },
                  { label: 'Paid Out', value: funnel.paid_out, color: 'bg-green-500', width: funnel.total_referrals ? (funnel.paid_out / funnel.total_referrals) * 100 : 0 },
                ].map((step, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-700">{step.label}</span>
                      <span className="font-bold text-gray-900">{step.value}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-3">
                      <div className={`${step.color} rounded h-3 transition-all`} style={{width: `${Math.max(step.width, 4)}%`}} />
                    </div>
                  </div>
                ))}
              </div>
              {funnel.total_referrals > 0 && funnel.paid_out < funnel.signed_up && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    <strong>Drop-off detected:</strong> {funnel.signed_up - funnel.paid_out} referrals haven't completed their journey yet. This is where you're losing money.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Breakdown */}
          <Card data-testid="referral-breakdown">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Referral Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg text-center border-cyan-200 bg-cyan-50/50">
                  <Users className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-cyan-700">{breakdown.talent_referrals}</p>
                  <p className="text-sm text-gray-500">Talent Referrals</p>
                  <p className="text-xs text-cyan-600 mt-1">$25 each</p>
                </div>
                <div className="p-4 border rounded-lg text-center border-violet-200 bg-violet-50/50">
                  <Building2 className="w-6 h-6 text-violet-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-violet-700">{breakdown.client_referrals}</p>
                  <p className="text-sm text-gray-500">Client Referrals</p>
                  <p className="text-xs text-violet-600 mt-1">$100 each</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">Total Platform Referral Value</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  ${((breakdown.talent_referrals || 0) * 25 + (breakdown.client_referrals || 0) * 100).toFixed(2)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Referrers */}
        <Card data-testid="top-referrers">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm font-semibold">Top Referrers</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {top_referrers.length === 0 ? (
              <div className="text-center py-8">
                <Crown className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No referrers yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {top_referrers.map((r, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${i === 0 ? 'border-amber-200 bg-amber-50' : ''}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{r.name}</p>
                        <Badge className="text-[9px] bg-gray-100 text-gray-500">{r.role}</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">${r.earned.toFixed(2)} earned</p>
                      <p className="text-[10px] text-amber-600">${r.pending.toFixed(2)} pending</p>
                      <p className="text-[10px] text-gray-400">{r.total_referrals} referrals</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReferralLeaderboard;
